/**
 * Publish beyond Console: Git, GitOps and the image registry, straight to vfs-service (no BFF), the same way the
 * Quality tab talks to it (QualityService.js): its base URL, the authorized fetch and the ACL headers.
 *
 * - The sandbox pod (`/api/v1/projects/:id/publish/...`): `targets`, `deploy` (the generated Helm chart and compose
 *   file), `git`, `gitops`, `releases`. Git and GitOps record their release themselves.
 * - The gateway, in the same URL space: `registry` (configured by the Flow admin), `images` (a build and push run),
 *   `images/runs/:id` (its progress). It records the release when the run ends.
 *
 * Errors are DataErrors: `code`, `status`, `body`, plus `blocking` (409 `quality_gate_blocked`: the Publish dialog shows
 * its gate banner) and `connectUrl` (403 `no_account`). A 404 without a JSON body means an older sandbox
 * (`code: 'sandbox_outdated'`).
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { DataError, fail } from './DataService';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const publishUrl = (projectId, path = '') => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}/publish${path}`;

async function call(url, { method = 'GET', body } = {}) {
  if (!base()) { throw new DataError('vfs-service is not configured', 0, null); }
  const json = body !== undefined;
  const response = await getAuthorizedFetch()(url, {
    method,
    headers: { Accept: 'application/json', ...buildAclContextHeaders(), ...(json ? { 'Content-Type': 'application/json' } : {}) },
    ...(json ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    const error = await fail(response);
    if (Array.isArray(error.body?.blocking)) { error.blocking = error.body.blocking; }
    if (error.body?.connectUrl) { error.connectUrl = error.body.connectUrl; }
    throw error;
  }
  if (response.status === 204) { return {}; }
  return (await response.json().catch(() => null)) || {};
}

/** What this Flow can publish to: `{ git: { enabled, providers, accounts, connectUrl }, registry, deploy }`. */
export function getPublishTargets(projectId) {
  return call(publishUrl(projectId, '/targets'));
}

/**
 * The deployment files that would be published (a preview, never gated): `{ name, version, services, stores, chart:
 * { name, files: [{ path, content }] }, compose: { path, content }, gitops: { files }, notes }`.
 */
export function getDeployFiles(projectId, { environment, images } = {}) {
  const search = new URLSearchParams();
  if (environment) { search.set('environment', environment); }
  if (images) { search.set('images', JSON.stringify(images)); }
  const text = search.toString();
  return call(publishUrl(projectId, `/deploy${text ? `?${text}` : ''}`));
}

/**
 * Pushes the project to a Git repository: `{ repoUrl }` (an existing one) or `{ create: { provider, name, namespace?,
 * host?, private, project? } }`, `branch?`, `newBranch?`, `mergeRequest?`, `include: { deploy }`, `account?`.
 * → `{ repoUrl, webUrl, branch, commit, filesPushed, created, mergeRequest: { url, number } | null, compareUrl, account }`.
 */
export function publishToGit(projectId, body) {
  return call(publishUrl(projectId, '/git'), { method: 'POST', body });
}

/** The GitOps layout only (chart, environments/<env>/values.yaml, argocd/, flux/): the body of publishToGit plus `environments`. */
export function publishToGitOps(projectId, body) {
  return call(publishUrl(projectId, '/gitops'), { method: 'POST', body });
}

/** The project's publishes, newest first: `{ releases: [{ id, at, by, kind, target, url?, branch?, mergeRequestUrl?, chart?, images? }] }`. */
export function listReleases(projectId) {
  return call(publishUrl(projectId, '/releases'));
}

/** Records a publish (the server does it for Git, GitOps and the registry: kept for other callers). */
export function recordPublishRelease(projectId, release) {
  return call(publishUrl(projectId, '/releases'), { method: 'POST', body: release });
}

/** The gateway: the image registry the Flow admin set up, `{ configured, registry, builder: { available, reason? } }`. */
export function getRegistry(projectId) {
  return call(publishUrl(projectId, '/registry'));
}

/** The gateway: builds, signs and pushes the images (and the chart). `{ run: { id, state: 'queued', … } }` (202). */
export function publishImages(projectId, { services, platforms, tag, chart } = {}) {
  const body = {};
  if (services?.length) { body.services = services; }
  if (platforms?.length) { body.platforms = platforms; }
  if (tag) { body.tag = tag; }
  if (typeof chart === 'boolean') { body.chart = chart; }
  return call(publishUrl(projectId, '/images'), { method: 'POST', body });
}

/** The gateway: one run, `{ run: { id, state, services: [{ name, state, image, digest, error? }], chart, error?, startedAt, finishedAt } }`. */
export function getImageRun(projectId, runId) {
  return call(publishUrl(projectId, `/images/runs/${encodeURIComponent(runId)}`));
}

const pad = (n) => String(n).padStart(2, '0');

/** The new branch a publish pushes to when it opens a merge request: `flow/publish-20261030-1530` (local time). */
export function publishBranchName(date = new Date()) {
  return `flow/publish-${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}`;
}

/** A run that is over. */
export const RUN_DONE = ['succeeded', 'failed'];
